import { useCallback, useEffect, useMemo, useState } from "react";
import { api, fmtMoney } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, RefreshCw, DollarSign, Users, TrendingUp, AlertTriangle, Calculator, Target, ArrowRight, Webhook, CheckCircle2, XCircle, Clock, FileDown } from "lucide-react";

const TIER_BADGE = {
  free: "bg-slate-200 text-slate-700",
  pro: "bg-emerald-100 text-emerald-700",
  hustler: "bg-sky-100 text-sky-700",
  team: "bg-indigo-100 text-indigo-700",
};

export default function AdminRevenue() {
  const [data, setData] = useState(null);
  const [tx, setTx] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [funnels, setFunnels] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [r1, r2, r3, r4] = await Promise.all([
        api.get("/admin/revenue"),
        api.get("/admin/revenue/recent-transactions", { params: { limit: 15 } }),
        api.get("/billing/tiers"),
        api.get("/admin/email-funnels", { params: { days: 90 } }).catch(() => ({ data: { funnels: [] } })),
      ]);
      setData(r1.data);
      setTx(r2.data || []);
      setTiers(r3.data || []);
      setFunnels(r4.data?.funnels || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load revenue");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  if (loading || !data) {
    return (
      <div className="card p-8 text-center text-[var(--ink-2)]">
        <Loader2 size={20} className="animate-spin inline" /> Loading revenue…
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4" data-testid="admin-revenue">
      <div className="flex justify-end gap-2">
        <button
          type="button"
          className="btn btn-money"
          onClick={async () => {
            try {
              const r = await api.get("/admin/revenue/projection.pdf", { responseType: "blob" });
              const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
              const a = document.createElement("a");
              a.href = url;
              a.download = `iSpeedToClose-MRR-Projection-${new Date().toISOString().slice(0, 10)}.pdf`;
              document.body.appendChild(a);
              a.click();
              a.remove();
              window.URL.revokeObjectURL(url);
              toast.success("MRR Projection PDF downloaded");
            } catch (err) {
              toast.error(err?.response?.data?.detail || "PDF generation failed");
            }
          }}
          data-testid="admin-revenue-download-projection"
          title="One-page investor-ready PDF with live MRR + 3 growth scenarios"
        >
          <FileDown size={14} /> Projection PDF
        </button>
        <button type="button" className="btn btn-ghost" onClick={refresh} data-testid="admin-revenue-refresh">
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat icon={DollarSign} label="MRR" value={fmtMoney(data.mrr_usd)} accent="emerald" testid="stat-mrr" />
        <Stat icon={TrendingUp} label="ARR" value={fmtMoney(data.arr_usd)} accent="sky" testid="stat-arr" />
        <Stat icon={Users} label="Paying users" value={data.paying_users} accent="violet" testid="stat-paying" />
        <Stat icon={AlertTriangle} label="Churn (30d)" value={data.churned_last_30d} accent="rose" testid="stat-churn" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <div className="card p-4" data-testid="admin-revenue-tiers">
          <div className="text-xs text-[var(--ink-2)] uppercase tracking-wide mb-2">Subscribers by tier</div>
          <div className="flex flex-col gap-2">
            {data.by_tier.map((t) => (
              <div key={t.key} className="flex items-center justify-between gap-3">
                <span className={"px-2 py-0.5 rounded text-xs font-semibold " + (TIER_BADGE[t.key] || "bg-slate-200")}>
                  {t.name}
                </span>
                <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden mx-2">
                  <div
                    className="h-full bg-[var(--primary)]"
                    style={{ width: `${Math.min(100, (t.count / Math.max(data.total_users, 1)) * 100)}%` }}
                  />
                </div>
                <div className="text-sm tabular-nums w-16 text-right">{t.count}</div>
                <div className="text-xs text-[var(--ink-2)] tabular-nums w-20 text-right">{fmtMoney(t.mrr_usd)}/mo</div>
              </div>
            ))}
          </div>
          <div className="mt-3 pt-3 border-t text-xs text-[var(--ink-2)] flex justify-between">
            <span>Total users</span>
            <strong className="text-[var(--ink)]">{data.total_users}</strong>
          </div>
          <div className="text-xs text-[var(--ink-2)] flex justify-between">
            <span>Trialing</span>
            <strong className="text-[var(--ink)]">{data.trialing}</strong>
          </div>
          <div className="text-xs text-[var(--ink-2)] flex justify-between">
            <span>Cancel at period end</span>
            <strong className="text-[var(--ink)]">{data.cancel_pending}</strong>
          </div>
        </div>

        <div className="card p-4" data-testid="admin-revenue-transactions">
          <div className="text-xs text-[var(--ink-2)] uppercase tracking-wide mb-2">Recent transactions</div>
          {tx.length === 0 ? (
            <div className="text-sm text-[var(--ink-3)] py-6 text-center">No transactions yet.</div>
          ) : (
            <div className="flex flex-col gap-2 max-h-[420px] overflow-y-auto">
              {tx.map((t) => (
                <div key={t.session_id} className="flex items-center justify-between text-sm border-b pb-2 last:border-b-0">
                  <div className="min-w-0">
                    <div className="font-semibold truncate max-w-[200px]">{t.user_email || "—"}</div>
                    <div className="text-xs text-[var(--ink-2)]">
                      {t.tier} · {t.billing_cycle} · <span className={t.status === "completed" ? "text-emerald-700" : "text-amber-700"}>{t.status}</span>
                    </div>
                  </div>
                  <div className="text-xs text-[var(--ink-3)] text-right">
                    {t.created_at ? new Date(t.created_at).toLocaleDateString() : "—"}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <MRRProjector tiers={tiers} funnels={funnels} />
      <WebhookHealth />
    </div>
  );
}

function MRRProjector({ tiers, funnels }) {
  // Filter to paid tiers (free is excluded — no revenue contribution)
  const paid = useMemo(() => (tiers || []).filter((t) => t.monthly_usd > 0), [tiers]);
  const [totalSubs, setTotalSubs] = useState(250);
  // mix: percentage of total subs per tier key. Always sums to 100.
  const [mix, setMix] = useState({});

  // Initialize mix once tiers load — default: 60/30/10 (Pro/Hustler/Teams) if 3 paid tiers
  useEffect(() => {
    if (paid.length === 0 || Object.keys(mix).length === paid.length) return;
    const defaults = { pro: 60, hustler: 30, team: 10 };
    const init = {};
    let allocated = 0;
    paid.forEach((t, i) => {
      if (i === paid.length - 1) init[t.key] = 100 - allocated;
      else { init[t.key] = defaults[t.key] ?? Math.floor(100 / paid.length); allocated += init[t.key]; }
    });
    setMix(init);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paid]);

  // Calculation: derive subs per tier and MRR contribution
  const projection = useMemo(() => {
    const rawRows = paid.map((t) => {
      const pct = mix[t.key] ?? 0;
      const count = Math.round((pct / 100) * totalSubs);
      return { ...t, pct, count };
    });
    // Reconcile rounding so counts sum to totalSubs (return NEW array)
    const totalCount = rawRows.reduce((s, r) => s + r.count, 0);
    const diff = totalSubs - totalCount;
    const rows = rawRows.map((r, i) => {
      const adjustedCount = i === rawRows.length - 1 ? r.count + diff : r.count;
      return { ...r, count: adjustedCount, mrr: adjustedCount * r.monthly_usd };
    });
    const mrr = rows.reduce((s, r) => s + r.mrr, 0);
    const arr = rows.reduce((s, r) => s + r.count * r.annual_usd, 0);
    const arpu = totalSubs > 0 ? mrr / totalSubs : 0;
    return { rows, mrr, arr_monthly: mrr * 12, arr_if_all_annual: arr, arpu };
  }, [paid, mix, totalSubs]);

  // Adjust one tier %, distribute the delta across others proportionally so the sum stays at 100
  const updateMix = (key, newPct) => {
    newPct = Math.max(0, Math.min(100, Math.round(newPct)));
    const others = paid.map((t) => t.key).filter((k) => k !== key);
    const currentOthersSum = others.reduce((s, k) => s + (mix[k] ?? 0), 0);
    const remaining = 100 - newPct;
    const next = { ...mix, [key]: newPct };
    if (currentOthersSum > 0 && others.length > 0) {
      // Scale others proportionally
      let allocated = 0;
      others.forEach((k, i) => {
        if (i === others.length - 1) next[k] = remaining - allocated;
        else {
          const share = Math.round((mix[k] / currentOthersSum) * remaining);
          next[k] = share;
          allocated += share;
        }
      });
    } else if (others.length > 0) {
      // All others were 0 — split remaining evenly
      const each = Math.floor(remaining / others.length);
      others.forEach((k, i) => {
        next[k] = i === others.length - 1 ? remaining - each * (others.length - 1) : each;
      });
    }
    setMix(next);
  };

  if (paid.length === 0) return null;

  return (
    <div className="card p-5" data-testid="mrr-projector">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-lg bg-[var(--ink)] text-white flex items-center justify-center">
          <Calculator size={18} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-display font-extrabold text-lg text-[var(--ink)]">MRR Projector</h3>
          <p className="text-xs text-[var(--ink-2)] mt-0.5">
            What-if calculator using your <strong>live tier prices</strong>. Slide subscriber count + tier mix to model
            target ARR.
          </p>
        </div>
      </div>

      <div className="grid lg:grid-cols-[280px_1fr] gap-5">
        {/* Inputs */}
        <div className="space-y-4">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[var(--ink-3)] font-semibold flex items-center justify-between">
              <span>Total paying subscribers</span>
              <span className="text-[var(--ink)] text-sm font-display font-bold">{totalSubs.toLocaleString()}</span>
            </label>
            <input
              type="range"
              min={10}
              max={5000}
              step={10}
              value={totalSubs}
              onChange={(e) => setTotalSubs(parseInt(e.target.value, 10))}
              className="w-full mt-1 accent-[var(--primary)]"
              data-testid="proj-total-subs"
            />
            <div className="flex gap-1 mt-2">
              {[100, 250, 500, 1000, 2500].map((n) => (
                <button
                  key={n}
                  onClick={() => setTotalSubs(n)}
                  className={
                    "flex-1 text-[10px] px-2 py-1 rounded-md font-semibold transition-all " +
                    (n === totalSubs
                      ? "bg-[var(--ink)] text-white"
                      : "bg-[var(--bg)] text-[var(--ink-2)] hover:bg-[var(--line)]")
                  }
                  data-testid={`proj-preset-${n}`}
                >
                  {n.toLocaleString()}
                </button>
              ))}
            </div>
          </div>

          <div className="pt-2 border-t border-[var(--line)]">
            <div className="text-[10px] uppercase tracking-widest text-[var(--ink-3)] font-semibold mb-2">Tier mix</div>
            {paid.map((t) => (
              <div key={t.key} className="mb-3" data-testid={`proj-mix-${t.key}`}>
                <div className="flex items-center justify-between text-xs mb-1">
                  <span className="font-semibold text-[var(--ink)]">{t.name}</span>
                  <span className="text-[var(--ink-2)] tabular-nums">
                    {mix[t.key] ?? 0}% · {fmtMoney(t.monthly_usd)}/mo
                  </span>
                </div>
                <input
                  type="range"
                  min={0}
                  max={100}
                  step={1}
                  value={mix[t.key] ?? 0}
                  onChange={(e) => updateMix(t.key, parseInt(e.target.value, 10))}
                  className="w-full accent-[var(--primary)]"
                />
              </div>
            ))}
            <div className="text-[10px] text-[var(--ink-3)] italic mt-1">
              Sliders auto-balance to sum 100%.
            </div>
          </div>
        </div>

        {/* Outputs */}
        <div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
            <ProjStat label="Projected MRR" value={fmtMoney(projection.mrr)} accent="emerald" big />
            <ProjStat label="ARR (12 × MRR)" value={fmtMoney(projection.arr_monthly)} accent="sky" big />
            <ProjStat label="ARPU" value={fmtMoney(Math.round(projection.arpu))} accent="slate" />
            <ProjStat label="Annual upfront (if 100% annual)" value={fmtMoney(projection.arr_if_all_annual)} accent="violet" />
          </div>

          {/* Tier breakdown table */}
          <div className="bg-[var(--bg)] rounded-lg border border-[var(--line)] overflow-hidden">
            <div className="px-4 py-2 text-[10px] uppercase tracking-widest text-[var(--ink-3)] font-semibold border-b border-[var(--line)]">
              Tier breakdown
            </div>
            {projection.rows.map((r) => (
              <div
                key={r.key}
                className="px-4 py-3 flex items-center gap-3 border-b border-[var(--line)] last:border-b-0"
                data-testid={`proj-row-${r.key}`}
              >
                <div className="w-24 font-semibold text-sm text-[var(--ink)]">{r.name}</div>
                <div className="flex-1 h-2 bg-white rounded-full overflow-hidden border border-[var(--line)]">
                  <div className="h-full bg-[var(--primary)]" style={{ width: `${r.pct}%` }} />
                </div>
                <div className="w-10 text-right text-xs font-semibold text-[var(--ink-2)] tabular-nums">{r.pct}%</div>
                <div className="w-16 text-right text-sm font-display font-bold tabular-nums">{r.count.toLocaleString()}</div>
                <div className="w-24 text-right text-sm font-display font-bold text-[var(--primary-dark)] tabular-nums">
                  {fmtMoney(r.mrr)}
                </div>
              </div>
            ))}
          </div>

          <TargetTrajectory arpu={projection.arpu} funnels={funnels} />
        </div>
      </div>
    </div>
  );
}

function TargetTrajectory({ arpu, funnels }) {
  const [goalMRR, setGoalMRR] = useState(50000);

  // Pull real trial→paid conversion from the funnels endpoint (kind = trial_ending)
  const trialFunnel = (funnels || []).find((f) => f.kind === "trial_ending");
  const trialConvRate = trialFunnel?.rates?.conversion ?? 0; // percentage (0-100)
  const trialSampleSize = trialFunnel?.sent ?? 0;
  const hasRealData = trialSampleSize >= 10;

  // Fallback to a sane industry default if no real data yet (B2B SaaS trial→paid ≈ 18%)
  const effectiveConvRate = hasRealData ? trialConvRate : 18;

  const subsNeeded = arpu > 0 ? Math.ceil(goalMRR / arpu) : 0;
  const trialsNeeded = effectiveConvRate > 0 ? Math.ceil((subsNeeded * 100) / effectiveConvRate) : 0;
  const trialsPerWeek = Math.ceil(trialsNeeded / 12); // 12-week ramp
  const trialsPerDay = Math.ceil(trialsNeeded / 90); // 90-day ramp

  const arpuMonthly = Math.round(arpu);

  return (
    <div className="mt-5 p-5 rounded-lg bg-gradient-to-br from-[var(--ink)] to-[#1E293B] text-white" data-testid="target-trajectory">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-lg bg-[var(--primary)] text-white flex items-center justify-center">
          <Target size={18} />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="font-display font-extrabold text-lg">Target Trajectory</h4>
          <p className="text-xs text-slate-300 mt-0.5">
            Set a goal MRR and we&apos;ll back-solve subs + trials needed using your{" "}
            <strong className="text-white">{hasRealData ? `real ${trialConvRate.toFixed(1)}% trial→paid` : "18% industry default"}</strong>{" "}
            conversion rate at the current tier mix&apos;s <strong className="text-white">{fmtMoney(arpuMonthly)} ARPU</strong>.
          </p>
        </div>
      </div>

      <div className="grid lg:grid-cols-[280px_1fr] gap-5">
        {/* Goal input */}
        <div>
          <label className="text-[10px] uppercase tracking-widest text-slate-300 font-semibold flex items-center justify-between">
            <span>Goal MRR</span>
            <span className="text-white text-sm font-display font-bold">{fmtMoney(goalMRR)}</span>
          </label>
          <input
            type="range"
            min={1000}
            max={500000}
            step={1000}
            value={goalMRR}
            onChange={(e) => setGoalMRR(parseInt(e.target.value, 10))}
            className="w-full mt-1 accent-[var(--primary)]"
            data-testid="target-goal-mrr"
          />
          <div className="flex flex-wrap gap-1 mt-2">
            {[10000, 25000, 50000, 100000, 250000].map((n) => (
              <button
                key={n}
                onClick={() => setGoalMRR(n)}
                className={
                  "flex-1 min-w-0 text-[10px] px-2 py-1 rounded-md font-semibold transition-all " +
                  (n === goalMRR
                    ? "bg-[var(--primary)] text-white"
                    : "bg-white/10 text-slate-200 hover:bg-white/20")
                }
                data-testid={`target-preset-${n}`}
              >
                {n >= 1000 ? `$${(n / 1000)}k` : `$${n}`}
              </button>
            ))}
          </div>

          {!hasRealData && (
            <div className="mt-3 p-2.5 rounded-md bg-amber-500/15 border border-amber-400/30 text-[11px] text-amber-100 leading-relaxed">
              <strong>Heads-up:</strong> only {trialSampleSize} trial-ending sends so far — using 18% industry default
              until you have ≥10. Real data takes over automatically once it&apos;s there.
            </div>
          )}
        </div>

        {/* The trajectory chain */}
        <div className="grid sm:grid-cols-[1fr_auto_1fr_auto_1fr] gap-3 items-center">
          <TrajStat label="MRR Goal" value={fmtMoney(goalMRR)} icon={DollarSign} accent="primary" />
          <ArrowRight size={18} className="text-slate-400 hidden sm:block mx-auto" />
          <TrajStat
            label="Paying subs needed"
            value={subsNeeded.toLocaleString()}
            icon={Users}
            sublabel={`at ${fmtMoney(arpuMonthly)} ARPU`}
            accent="sky"
          />
          <ArrowRight size={18} className="text-slate-400 hidden sm:block mx-auto" />
          <TrajStat
            label="Trials needed"
            value={trialsNeeded.toLocaleString()}
            icon={Target}
            sublabel={`at ${effectiveConvRate.toFixed(1)}% conv`}
            accent="amber"
          />
        </div>
      </div>

      {/* Ramp cadence */}
      {trialsNeeded > 0 && (
        <div className="mt-5 pt-4 border-t border-white/10">
          <div className="text-[10px] uppercase tracking-widest text-slate-300 font-semibold mb-3">
            Acquisition cadence to hit your goal
          </div>
          <div className="grid grid-cols-3 gap-3">
            <CadenceStat label="Per day · 90-day ramp"    value={trialsPerDay} suffix="trials/day" />
            <CadenceStat label="Per week · 12-week ramp"  value={trialsPerWeek} suffix="trials/week" />
            <CadenceStat label="Total · in 12 months"     value={Math.ceil(trialsNeeded / 12)} suffix="trials/month" />
          </div>
        </div>
      )}
    </div>
  );
}

function TrajStat({ icon: Icon, label, value, sublabel, accent }) {
  const palette =
    accent === "primary" ? "bg-[var(--primary)]/20 text-[var(--primary)]" :
    accent === "sky"     ? "bg-sky-500/20 text-sky-300" :
    accent === "amber"   ? "bg-amber-500/20 text-amber-300" :
                           "bg-white/10 text-slate-300";
  return (
    <div className="bg-white/5 border border-white/10 rounded-lg p-4">
      <div className={"inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[10px] uppercase tracking-widest font-semibold " + palette}>
        <Icon size={11} /> {label}
      </div>
      <div className="font-display font-extrabold text-2xl text-white mt-2 tabular-nums">{value}</div>
      {sublabel && <div className="text-[11px] text-slate-300 mt-1">{sublabel}</div>}
    </div>
  );
}

function CadenceStat({ label, value, suffix }) {
  return (
    <div className="bg-white/5 border border-white/10 rounded-md p-3">
      <div className="text-[10px] uppercase tracking-widest text-slate-300 font-semibold">{label}</div>
      <div className="font-display font-extrabold text-lg text-white mt-1 tabular-nums">
        {value.toLocaleString()} <span className="text-xs text-slate-300 font-normal">{suffix}</span>
      </div>
    </div>
  );
}

function ProjStat({ label, value, accent, big }) {
  const palette = {
    emerald: "bg-emerald-50 text-emerald-700",
    sky:     "bg-sky-50 text-sky-700",
    violet:  "bg-violet-50 text-violet-700",
    slate:   "bg-slate-50 text-slate-700",
  }[accent] || "bg-slate-50 text-slate-700";
  return (
    <div className="card p-3">
      <div className={"inline-block text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-md " + palette}>
        {label}
      </div>
      <div className={"font-display font-extrabold mt-2 tabular-nums " + (big ? "text-2xl" : "text-lg")}>{value}</div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, accent, testid }) {
  const palette = {
    emerald: "bg-emerald-50 text-emerald-700",
    sky: "bg-sky-50 text-sky-700",
    violet: "bg-violet-50 text-violet-700",
    rose: "bg-rose-50 text-rose-700",
  }[accent] || "bg-slate-50 text-slate-700";
  return (
    <div className="card p-4" data-testid={testid}>
      <div className={"w-9 h-9 rounded-lg flex items-center justify-center mb-2 " + palette}>
        <Icon size={16} />
      </div>
      <div className="text-xs text-[var(--ink-2)] uppercase tracking-wide">{label}</div>
      <div className="font-display font-black text-2xl mt-1">{value}</div>
    </div>
  );
}


// ─── Stripe Webhook Health ───────────────────────────────────────────────────

function WebhookHealth() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/billing/webhook-health");
      setData(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load webhook health");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const healthy = data && data.failure_24h === 0 && data.success_24h > 0;
  const stale = data && data.total_24h === 0;
  const degraded = data && data.failure_24h > 0;

  let statusLabel = "Idle";
  let statusClass = "bg-slate-100 text-slate-700";
  let StatusIcon = Clock;
  if (healthy) { statusLabel = "Healthy"; statusClass = "bg-emerald-100 text-emerald-700"; StatusIcon = CheckCircle2; }
  else if (degraded) { statusLabel = "Degraded"; statusClass = "bg-rose-100 text-rose-700"; StatusIcon = XCircle; }
  else if (stale) { statusLabel = "No events (24h)"; statusClass = "bg-amber-100 text-amber-700"; StatusIcon = Clock; }

  return (
    <div className="card p-4" data-testid="admin-webhook-health">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-indigo-50 text-indigo-700">
            <Webhook size={16} />
          </div>
          <div>
            <div className="text-xs text-[var(--ink-2)] uppercase tracking-wide">Stripe webhook health</div>
            <div className="font-display font-black text-lg leading-tight">Billing pipeline</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className={"px-2 py-1 rounded-full text-xs font-semibold inline-flex items-center gap-1 " + statusClass} data-testid="webhook-health-status">
            <StatusIcon size={12} /> {statusLabel}
          </span>
          <button type="button" className="btn btn-ghost" onClick={refresh} data-testid="webhook-health-refresh">
            <RefreshCw size={14} /> Refresh
          </button>
        </div>
      </div>

      {loading || !data ? (
        <div className="py-6 text-center text-sm text-[var(--ink-3)]">
          <Loader2 size={16} className="animate-spin inline" /> Loading…
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
            <MiniStat label="Success (24h)" value={data.success_24h} accent="emerald" testid="webhook-stat-success" />
            <MiniStat label="Failed (24h)" value={data.failure_24h} accent={data.failure_24h > 0 ? "rose" : "slate"} testid="webhook-stat-failed" />
            <MiniStat label="Last success" value={data.last_success?.processed_at ? relTime(data.last_success.processed_at) : "Never"} accent="sky" testid="webhook-stat-last-success" />
            <MiniStat label="Last failure" value={data.last_failure?.processed_at ? relTime(data.last_failure.processed_at) : "—"} accent={data.last_failure ? "amber" : "slate"} testid="webhook-stat-last-failure" />
          </div>

          {data.last_failure && (
            <div className="mb-3 p-3 rounded-lg bg-rose-50 border border-rose-200 text-xs text-rose-800" data-testid="webhook-last-error">
              <div className="font-semibold mb-0.5">
                Last error · {data.last_failure.event_type || "unknown"} · {data.last_failure.event_id || "—"}
              </div>
              <div className="font-mono break-all opacity-80">{data.last_failure.error}</div>
            </div>
          )}

          <div className="text-xs text-[var(--ink-2)] uppercase tracking-wide mb-2">Recent events</div>
          {data.recent.length === 0 ? (
            <div className="text-sm text-[var(--ink-3)] py-6 text-center">
              No webhook events yet. Once Stripe delivers one, it&apos;ll appear here.
            </div>
          ) : (
            <div className="flex flex-col gap-1.5 max-h-[320px] overflow-y-auto">
              {data.recent.map((ev, i) => (
                <div
                  key={(ev.event_id || "x") + i}
                  className="flex items-center justify-between text-sm border-b last:border-b-0 pb-1.5"
                  data-testid="webhook-event-row"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    {ev.status === "ok"
                      ? <CheckCircle2 size={14} className="text-emerald-600 shrink-0" />
                      : <XCircle size={14} className="text-rose-600 shrink-0" />}
                    <div className="min-w-0">
                      <div className="font-mono text-xs truncate max-w-[280px]">{ev.event_type || "unknown"}</div>
                      <div className="text-[10px] text-[var(--ink-3)] truncate">{ev.event_id || "—"}</div>
                    </div>
                  </div>
                  <div className="text-[10px] text-[var(--ink-3)] tabular-nums shrink-0 ml-2">
                    {ev.processed_at ? relTime(ev.processed_at) : "—"}
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="mt-3 pt-3 border-t text-[10px] text-[var(--ink-3)]">
            Endpoint: <code className="font-mono">/api/billing/webhook</code> (alias of <code className="font-mono">/api/webhook/stripe</code>)
          </div>
        </>
      )}
    </div>
  );
}

function MiniStat({ label, value, accent, testid }) {
  const palette = {
    emerald: "bg-emerald-50 text-emerald-700",
    sky: "bg-sky-50 text-sky-700",
    rose: "bg-rose-50 text-rose-700",
    amber: "bg-amber-50 text-amber-700",
    slate: "bg-slate-50 text-slate-700",
  }[accent] || "bg-slate-50 text-slate-700";
  return (
    <div className={"rounded-lg p-3 " + palette} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wide opacity-80">{label}</div>
      <div className="font-display font-black text-xl mt-0.5 tabular-nums">{value}</div>
    </div>
  );
}

function relTime(iso) {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "—";
  const diff = Date.now() - then;
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  return `${day}d ago`;
}
